import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import { ArrowRight, Sparkles, Plane, CheckCircle2, ShieldCheck, Layers, Target, Type } from "lucide-react";

const HIGHLIGHTS = [
  { icon: Plane, title: "Automatic content creation", body: "Autopilot writes each caption from your business profile and brand voice — you never open a blank text box." },
  { icon: CheckCircle2, title: "Approval before posting (default)", body: "Every draft lands in your approval queue so you can review and edit before it publishes. One toggle switches Autopilot to fully automatic when you're ready." },
  { icon: Target, title: "Content Focus controls", body: "Tell Autopilot the mix of Promote, Educate, Brand awareness, and Community you want — it rotates categories accordingly instead of repeating one angle." },
  { icon: Type, title: "Post Length preference", body: "Set Standard, Long, or Detailed for the workspace so Autopilot's captions match the depth you want." },
  { icon: ShieldCheck, title: "Anti-repetition", body: "Postmoro tracks recent post takeaways, CTAs, and hook styles per workspace so Autopilot doesn't loop the same angle back to back." },
  { icon: Layers, title: "Per-workspace settings", body: "Business subscribers can run Autopilot with different schedules, focus, and post-length per workspace across up to four separate businesses." },
];

export default function AutopilotPublicPage() {
  return (
    <>
      <SEO
        title="Postmoro Autopilot | Automated Social Media Posting"
        description="Postmoro Autopilot automatically creates and schedules social media content while keeping posts varied, on-brand, and aligned with your business goals."
        path="/features/autopilot"
      />
      <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)]" data-testid="autopilot-public-page">
        <MarketingHeader activePath="/features" />

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" /> Autopilot
            </div>
            <h1 className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]" data-testid="autopilot-public-hero-title">
              Postmoro Autopilot — automated social media posting.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl">
              Autopilot plans a whole week at once. Pick the days and times you
              want to post, and Postmoro fills the calendar with drafts that
              match your brand voice and content mix. Review and approve — or
              flip one toggle to publish automatically.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/login"><Button size="lg" className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold">Start free <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
              <Link to="/pricing"><Button size="lg" variant="outline" className="border-[color:var(--border-subtle)] text-[color:var(--text-1)]">See pricing</Button></Link>
            </div>
          </div>
        </section>

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIGHLIGHTS.map((h) => (
              <div key={h.title} className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5" data-testid={`autopilot-highlight-${h.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
                  <h.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-base font-semibold">{h.title}</h3>
                <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">How it works</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Set it up once, adjust anytime</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-[color:var(--text-2)]">
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">1 · Pick your schedule</div>
              <p className="mt-2 leading-relaxed">Choose the days and time slots you want Autopilot to post. Every workspace can have its own cadence.</p>
            </div>
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">2 · Pick Content Focus &amp; length</div>
              <p className="mt-2 leading-relaxed">Tell Autopilot the mix of Promote / Educate / Brand awareness / Community and whether you want Standard, Long, or Detailed captions.</p>
            </div>
            <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
              <div className="font-display text-base font-semibold text-[color:var(--text-1)]">3 · Review or auto-publish</div>
              <p className="mt-2 leading-relaxed">Approve each draft from the queue, or switch Autopilot to fully automatic and it publishes on your schedule.</p>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
